import { useEffect, useMemo, useState } from 'react';

import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { uniqBy } from 'lodash';
import { useInView } from 'react-intersection-observer';

import { SparklesIcon } from '@/svgs/gradientIcons/sparklesIcon';

import {
  PatientListModesMap,
  RecentVisitsSortFilter,
  SortFilterEnum,
} from '../../../@types/appointments';
import { SummaryCard } from '../../../common-components/elements/SummaryCard/SummaryCard';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import { useRcmTreatmentsBatch } from '../../../react-query/rcm/useRcmTreatmentsBatch';
import { useAppointments } from '../../../react-query/useAppointments';
import type { Appointment } from '../../../react-query/useAppointments';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import type { TTreatmentResponse } from '../freeClaims/insuranceExport/types';
import {
  INITIAL_SELECTED_ANALYSES_STATE,
  modeAtom,
  searchAtom,
  selectDateAtom,
  selectedAnalysesAtom,
  sortFilterAtom,
} from '../../patientList/atoms';
import { PatientListPageLayout } from '../../patientList/PatientListPageLayout';
import { LoadingSpinner } from '../../patientList/styles';
import { PatientListPageLayoutContextMap } from '../../patientList/types';

import { ClaimsBanner } from './ClaimsBanner';
import { ClaimsTable } from './ClaimsTable';
import { filterAppointmentsByClaimStatus, getSortParam } from './helpers';
import { showClaimsBanner } from './state';

export const ClaimsPage = () => {
  // state
  const mode = useAtomValue(modeAtom);
  const search = useAtomValue(searchAtom);
  const sortFilter = useAtomValue(sortFilterAtom);
  const selectedDate = useAtomValue(selectDateAtom);

  const setSelectedAnalysesAtom = useSetAtom(selectedAnalysesAtom);
  const setSelectedDate = useSetAtom(selectDateAtom);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showBanner, setShowBanner] = useAtom(showClaimsBanner);
  const [selectedCard, setSelectedCard] = useState<'all' | 'ready' | 'review'>(
    'all',
  );

  // hooks
  const { t } = useLingui();
  const { debouncedValue: debouncedSearch } = useDebounce(search);
  const { ref: bottomAppointmentRef, inView: isBottomAppointmentInView } =
    useInView();

  const isSearchValid = debouncedSearch.length > 2;
  const today = useMemo(() => new Date(), []);

  let endDate: Date | undefined = endOfDay(today);
  let startDate: Date | undefined = undefined;
  let imagingStartDate: Date | undefined;
  let imagingEndDate: Date | undefined;
  if (selectedDate) {
    endDate = endOfDay(selectedDate);
  }
  if (isSearchValid) {
    endDate = undefined;
  }
  if (selectedDate) {
    startDate = startOfDay(selectedDate);
  }
  if (sortFilter === "Today's Radiographs") {
    imagingStartDate = startOfDay(today);
    imagingEndDate = endOfDay(today);
    startDate = startOfDay(today);
  }

  const {
    data: appointmentData,
    isLoading: isLoadingAppointments,
    isFetchingNextPage,
    fetchNextPage,
  } = useAppointments({
    appointmentOpts: {
      startDate,
      endDate,
      imagingStartDate,
      imagingEndDate,
      onlyImaging: mode === PatientListModesMap.RecentVisits && !isSearchValid,
    },
    patientOpts: {
      name: isSearchValid ? debouncedSearch : undefined,
    },
    meta: {
      sort: {
        appointmentOn: getSortParam(mode, sortFilter),
      },
      pagination: {
        limit:
          mode === PatientListModesMap.DailyHuddle ||
          sortFilter === "Today's Radiographs"
            ? undefined
            : 10,
      },
    },
  });

  const claimSummariesTreatmentData = useRcmTreatmentsBatch(appointmentData);

  // Process treatment data incrementally - don't wait for all batches
  const claimSummariesTreatments = useMemo(() => {
    const allTreatments: any[] = [];

    claimSummariesTreatmentData?.forEach((queryResult) => {
      if (queryResult.data && Array.isArray(queryResult.data)) {
        allTreatments.push(...queryResult.data);
      }
    });

    return allTreatments;
  }, [claimSummariesTreatmentData]);

  const allAppointments = useMemo(() => {
    setSelectedAnalysesAtom(INITIAL_SELECTED_ANALYSES_STATE);
    return appointmentData?.pages.flatMap((page) => page.data);
  }, [setSelectedAnalysesAtom, appointmentData]);

  const uniqueAppointments = useMemo(() => {
    return uniqBy(
      allAppointments,
      (appointment) => appointment.appointment.unifiedId,
    );
  }, [allAppointments]);

  // Generate an array of appointments with their treatments attached
  // This will now update incrementally as treatment data becomes available
  const filteredAppointmentsWithTreatments = useMemo<
    (Appointment & {
      treatments: TTreatmentResponse[];
    })[]
  >(() => {
    return (
      (uniqueAppointments || [])
        .map((appointment) => {
          // Find the matching treatment data for this appointment
          const matchingTreatmentData = claimSummariesTreatments?.find(
            (treatmentData) => {
              // Match by unifiedAppointmentId
              return (
                treatmentData?.unifiedAppointmentId &&
                appointment.appointment.unifiedId &&
                treatmentData.unifiedAppointmentId ===
                  parseInt(appointment.appointment.unifiedId)
              );
            },
          );

          // Get the treatments array from the matching treatment data
          const treatmentsArray = matchingTreatmentData?.treatments || [];

          // Return a new appointment object with treatments attached
          return {
            ...appointment,
            treatments: treatmentsArray,
          };
        })
        // Filter out appointments that don't have treatments
        .filter(
          (appointment) =>
            appointment.treatments && appointment.treatments.length > 0,
        )
    );
  }, [uniqueAppointments, claimSummariesTreatments]);

  const selectItems: React.ReactNode[] = [];

  Object.values(
    mode === PatientListModesMap.DailyHuddle
      ? SortFilterEnum
      : RecentVisitsSortFilter,
  ).forEach((option) =>
    selectItems.push(
      <div data-value={option} key={option}>
        {option}
      </div>,
    ),
  );

  const { nextCursor } =
    appointmentData?.pages[appointmentData.pages.length - 1] || {};

  // Check if any batch is still loading, but don't block the UI
  const isAnyBatchLoading =
    claimSummariesTreatmentData?.some((queryResult) => queryResult.isLoading) ||
    false;

  // Show loading when appointments are loading OR when we have appointments but no treatments yet
  const isLoading =
    isLoadingAppointments ||
    (!appointmentData && isAnyBatchLoading) ||
    (uniqueAppointments.length > 0 &&
      filteredAppointmentsWithTreatments.length === 0 &&
      isAnyBatchLoading);

  // Calculate summary card values
  const totalAppointmentsCount = filteredAppointmentsWithTreatments.length;
  const readyToSubmitCount = filteredAppointmentsWithTreatments.filter(
    (appointment) => filterAppointmentsByClaimStatus(appointment, 'ready'),
  ).length;
  const needsReviewCount = filteredAppointmentsWithTreatments.filter(
    (appointment) => filterAppointmentsByClaimStatus(appointment, 'review'),
  ).length;

  // Filter appointments for table based on selected card
  const filteredAppointmentsForTable =
    selectedCard === 'all'
      ? filteredAppointmentsWithTreatments
      : selectedCard === 'ready'
      ? filteredAppointmentsWithTreatments.filter((appointment) =>
          filterAppointmentsByClaimStatus(appointment, 'ready'),
        )
      : filteredAppointmentsWithTreatments.filter((appointment) =>
          filterAppointmentsByClaimStatus(appointment, 'review'),
        );

  const dateLocale = useGetDateFNSLocale();
  const dateFormatted =
    selectedDate &&
    format(selectedDate, 'MMMM d, yyyy', {
      locale: dateLocale,
    });

  // Calculate time saved based on number of appointments (7 minutes per appointment)
  const calculateTimeSaved = (appointmentCount: number): string => {
    const totalMinutes = appointmentCount * 7;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return t`~${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${
        minutes !== 1 ? 's' : ''
      }`;
    } else if (hours > 0) {
      return t`~${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      return t`~${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  };

  const timeSavedString = calculateTimeSaved(totalAppointmentsCount);

  // useEffects
  useEffect(() => {
    if (
      !!appointmentData &&
      appointmentData.pages.length > 0 &&
      isBottomAppointmentInView &&
      nextCursor &&
      appointmentData?.pages.length &&
      appointmentData?.pages.length <= 20
    ) {
      fetchNextPage();
    }
  }, [
    appointmentData,
    isBottomAppointmentInView,
    nextCursor,
    appointmentData?.pages.length,
    fetchNextPage,
  ]);

  useEffect(() => {
    if (!search && !selectedDate) {
      setSelectedDate(today);
    }
  }, [search, selectedDate, setSelectedDate, today]);

  return (
    <PatientListPageLayout
      context={PatientListPageLayoutContextMap.Claims}
      allAppointments={filteredAppointmentsWithTreatments}
      isLoadingAppointments={
        isLoadingAppointments ||
        (!appointmentData && isAnyBatchLoading) ||
        (uniqueAppointments.length > 0 &&
          filteredAppointmentsWithTreatments.length === 0 &&
          isAnyBatchLoading)
      }
      isLoadingVisitSummaries={isAnyBatchLoading}
    >
      <>
        {showBanner && !isLoading && !isSearchValid && (
          <ClaimsBanner
            timeSaved={timeSavedString}
            date={dateFormatted}
            onClose={() => setShowBanner(false)}
          />
        )}
        {!isLoading && !isSearchValid && (
          <div className="vai-w-full vai-flex vai-gap-4 vai-mb-6 vai-justify-between">
            {/* "All claims" card */}
            <SummaryCard
              icon={<SparklesIcon className="vai-w-6 vai-h-6" />}
              value={totalAppointmentsCount}
              label={t`Attachments Generated`}
              description={t`attachments generated with images & narratives`}
              color="blue"
              isSelected={selectedCard === 'all'}
              onClick={() => setSelectedCard('all')}
              testId="claims-summary-card"
            />
            {/* "Ready to submit" claims card */}
            <SummaryCard
              icon={
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="vai-text-green-600 vai-w-6 vai-h-6"
                />
              }
              value={readyToSubmitCount}
              label={t`Ready to Submit`}
              description={t`claim attachments are ready to download & submit`}
              color="green"
              isSelected={selectedCard === 'ready'}
              onClick={() => setSelectedCard('ready')}
              testId="claims-summary-card"
            />
            {/* "Needs review" claims card */}
            <SummaryCard
              icon={
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="vai-text-amber-500 vai-w-6 vai-h-6"
                />
              }
              value={needsReviewCount}
              label={t`Needs Review`}
              description={t`claim attachments require manual review`}
              color="yellow"
              isSelected={selectedCard === 'review'}
              onClick={() => setSelectedCard('review')}
              testId="claims-summary-card"
            />
          </div>
        )}
      </>

      {/* PLACEHOLDER DOWNLOAD MULTIPLE CLAIMS BUTTON */}
      {/* TODO: add download multiple claims button when the functionality is ready */}
      {/* <Button variant="outline" disabled={selectedRows.length === 0}>
        <FontAwesomeIcon icon={faArrowDownToLine} />
        Download {`(${selectedRows.length})`}
      </Button> */}

      {isLoading ? (
        <div className="vai-flex vai-justify-center vai-items-center">
          <LoadingSpinner className="vai-m-8" />
        </div>
      ) : (
        <div className="vai-max-h-[85vh]">
          {isLoadingAppointments ? (
            <div className="vai-flex vai-justify-center vai-items-center vai-h-full">
              <LoadingSpinner className="vai-m-8" />
            </div>
          ) : (
            <ClaimsTable
              appointments={filteredAppointmentsForTable}
              selectedRows={selectedRows}
              isSearchActive={isSearchValid}
              onSetSelectedRows={setSelectedRows}
            />
          )}

          <div ref={bottomAppointmentRef} />
          {isFetchingNextPage &&
            filteredAppointmentsWithTreatments?.length !== 0 && (
              <LoadingSpinner />
            )}
        </div>
      )}
    </PatientListPageLayout>
  );
};
